// src/components/CreateProgram.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import axios from 'axios';
import { apiErrorMessage } from './prescriptionForm';
import {
    EMPTY_PROGRAM_FORM,
    MIN_SESSIONS,
    MAX_SESSIONS,
    buildProgramRequest,
    sortProgramsByNewest,
} from './programForm';
import { starterNotice, hasStarters } from './exampleStarters';
import '../styles/CreateProgram.css';

// The stepped-wizard state that used to live here — `step`, `sessions`,
// `currentSession`, `currentExercise` and their setters — was never wired up.
// `step` was initialised to 1 and never changed, so `step === 1` was always
// true and the other three were pure dead weight (eight eslint warnings).
// Exercises are added in the program editor, which is where that flow actually
// lives; the wizard is not rebuilt here.

const CreateProgram = () => {
    const navigate = useNavigate();
    const { token } = useAuth();

    const [form, setForm] = useState(EMPTY_PROGRAM_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [programs, setPrograms] = useState(null); // null = not loaded yet
    const [listError, setListError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [duplicatingId, setDuplicatingId] = useState(null);
    const [examples, setExamples] = useState(null); // null = not loaded yet
    const [startingId, setStartingId] = useState(null);
    const [notice, setNotice] = useState(null);

    const fetchPrograms = useCallback(async () => {
        try {
            const response = await axios.get(buildApiUrl('/programs'), {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPrograms(sortProgramsByNewest(response.data));
            setListError(null);
        } catch (error) {
            // Previously a console.error, leaving an empty grid that looked like
            // "you have no programs".
            setPrograms([]);
            setListError(apiErrorMessage(error, 'Could not load your programs.'));
        }
    }, [token]);

    useEffect(() => {
        fetchPrograms();
    }, [fetchPrograms]);

    // The shared starter library. A failure here is silent — the section simply
    // does not render — because examples are a convenience, not the point of the
    // screen, and an error banner for them would sit above the user's own work.
    const fetchExamples = useCallback(async () => {
        try {
            const response = await axios.get(buildApiUrl('/example-programs'), {
                headers: { Authorization: `Bearer ${token}` },
            });
            setExamples(response.data?.programs ?? []);
        } catch (error) {
            setExamples([]);
        }
    }, [token]);

    useEffect(() => {
        fetchExamples();
    }, [fetchExamples]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setFieldErrors((prev) => {
            if (!(name in prev)) return prev;
            const { [name]: _dropped, ...rest } = prev;
            return rest;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { body, errors } = buildProgramRequest(form);
        if (errors) {
            setFieldErrors(errors);
            return;
        }

        setIsSubmitting(true);
        setFieldErrors({});
        setNotice(null);
        try {
            const response = await axios.post(buildApiUrl('/programs'), body, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const newId = response.data?.program?.id;
            if (newId) {
                navigate(`/programs/${newId}/edit`);
                return;
            }

            // A 201 without an id is a contract violation, not a success. It used
            // to fall through silently, leaving the user on a form that appeared
            // to have done nothing.
            setFieldErrors({
                form: 'The program was created but the server did not return its id. Check your programs below.',
            });
            await fetchPrograms();
        } catch (error) {
            setFieldErrors({ form: apiErrorMessage(error, 'Could not create the program.') });
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Delete a program.
     *
     * This handler existed but was never called — no control invoked it — and its
     * optimistic filter matched on `program.id`, which the old untagged list
     * response never carried (it emitted `ID`), so it could not have worked. It
     * is now wired to a control and refetches rather than filtering locally.
     */
    /**
     * Duplicate a programme.
     *
     * The name is left to the server, which numbers repeats — a coach planning
     * next cycle duplicates twice and would otherwise end up with two blocks of
     * the same name and no way to tell them apart.
     *
     * The copy carries the whole plan and none of the logged sets: a duplicate is
     * a plan, not a history.
     */
    const handleDuplicate = async (program) => {
        setDuplicatingId(program.id);
        setNotice(null);
        try {
            const { data } = await axios.post(
                buildApiUrl(`/programs/${program.id}/duplicate`), {},
                { headers: { Authorization: `Bearer ${token}` } });
            await fetchPrograms();
            setNotice({
                tone: 'success',
                message: `Created "${data.name}". Its training history starts empty.`,
            });
        } catch (error) {
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Could not duplicate that programme.'),
            });
        } finally {
            setDuplicatingId(null);
        }
    };

    /**
     * Copy an example into the caller's own account and open it.
     *
     * Mirrors handleDuplicate: the copy is a normal owned programme, so the next
     * screen is the editor, same as creating one.
     */
    const handleStart = async (example) => {
        setStartingId(example.id);
        setNotice(null);
        try {
            const { data } = await axios.post(
                buildApiUrl(`/example-programs/${example.id}/copy`), {},
                { headers: { Authorization: `Bearer ${token}` } });
            navigate(`/programs/${data.id}/edit`, {
                state: { notice: starterNotice(data) },
            });
        } catch (error) {
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Could not add that starter.'),
            });
            setStartingId(null);
        }
    };

    const handleDelete = async (program) => {
        const confirmed = window.confirm(
            `Delete "${program.name}"? This removes its sessions and all logged sets.`
        );
        if (!confirmed) return;

        setDeletingId(program.id);
        setNotice(null);
        try {
            await axios.delete(buildApiUrl(`/programs/${program.id}`), {
                headers: { Authorization: `Bearer ${token}` },
            });
            await fetchPrograms();
            setNotice({ tone: 'success', message: `Deleted "${program.name}".` });
        } catch (error) {
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Could not delete that program.'),
            });
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <BaseLayout>
            <div className="create-program">
                <header className="create-program-header">
                    <span className="pill">Programs</span>
                    <h1 className="page-title create-program-title">Create a program</h1>
                    <p className="meta-copy create-program-subtitle">
                        Sessions are created empty. Add exercises in the program editor.
                    </p>
                </header>

                <form className="card new-program-form" onSubmit={handleSubmit} noValidate>
                    {fieldErrors.form && <div className="status-error">{fieldErrors.form}</div>}

                    <div className="form-field">
                        <label htmlFor="program-name">Program name</label>
                        <input
                            id="program-name"
                            name="name"
                            type="text"
                            className={`field${fieldErrors.name ? ' field-invalid' : ''}`}
                            value={form.name}
                            onChange={handleChange}
                            placeholder="e.g. Winter Strength Block"
                            aria-invalid={fieldErrors.name ? 'true' : undefined}
                            aria-describedby={fieldErrors.name ? 'program-name-error' : undefined}
                        />
                        {fieldErrors.name && (
                            <span className="field-error" id="program-name-error">
                                {fieldErrors.name}
                            </span>
                        )}
                    </div>

                    <div className="form-field">
                        <label htmlFor="program-description">Description</label>
                        <textarea
                            id="program-description"
                            name="description"
                            className="field program-description-field"
                            value={form.description}
                            onChange={handleChange}
                            placeholder="What is this block for?"
                        />
                    </div>

                    <div className="form-field form-field-narrow">
                        <label htmlFor="program-sessions">Number of sessions</label>
                        <input
                            id="program-sessions"
                            name="numberOfSessions"
                            type="number"
                            min={MIN_SESSIONS}
                            max={MAX_SESSIONS}
                            className={`field${fieldErrors.numberOfSessions ? ' field-invalid' : ''}`}
                            value={form.numberOfSessions}
                            onChange={handleChange}
                            aria-invalid={fieldErrors.numberOfSessions ? 'true' : undefined}
                            aria-describedby={
                                fieldErrors.numberOfSessions
                                    ? 'program-sessions-error'
                                    : 'program-sessions-hint'
                            }
                        />
                        {fieldErrors.numberOfSessions ? (
                            <span className="field-error" id="program-sessions-error">
                                {fieldErrors.numberOfSessions}
                            </span>
                        ) : (
                            <span className="field-hint" id="program-sessions-hint">
                                {MIN_SESSIONS}–{MAX_SESSIONS}. Add more later in the editor.
                            </span>
                        )}
                    </div>

                    <div className="form-actions">
                        <button className="btn-primary" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating…' : 'Create program'}
                        </button>
                    </div>
                </form>

                {hasStarters(examples) && (
                    <section className="example-starters">
                        <h2 className="section-title example-starters-title">
                            Example programs — use as a starter
                        </h2>
                        <p className="meta-copy example-starters-subtitle">
                            Poliquin-style blocks with antagonist supersets, built from the
                            exercise library. Copying one makes an editable programme of your
                            own; the example stays put.
                        </p>
                        <ul className="program-grid">
                            {examples.map((example) => (
                                <li key={example.id} className="card program-card example-card">
                                    <span className="program-card-name">{example.name}</span>
                                    {example.description && (
                                        <span className="program-card-description">
                                            {example.description}
                                        </span>
                                    )}
                                    <div className="program-card-foot">
                                        <span className="meta-copy program-card-count">
                                            {example.sessionCount}{' '}
                                            {example.sessionCount === 1 ? 'session' : 'sessions'}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn-primary example-start-button"
                                            onClick={() => handleStart(example)}
                                            disabled={startingId === example.id}
                                        >
                                            {startingId === example.id ? 'Adding…' : 'Use as a starter'}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                <section className="program-library">
                    <h2 className="section-title program-library-title">Your programs</h2>

                    {notice && (
                        <div
                            className={notice.tone === 'error' ? 'status-error' : 'status-success'}
                            role="status"
                            aria-live="polite"
                        >
                            {notice.message}
                        </div>
                    )}

                    {listError && <div className="status-error">{listError}</div>}

                    {programs === null ? (
                        <p className="body-copy">Loading your programs…</p>
                    ) : programs.length === 0 ? (
                        !listError && (
                            <div className="callout-panel empty-library">
                                <p className="empty-library-text">
                                    No programs yet. Create your first one above.
                                </p>
                            </div>
                        )
                    ) : (
                        <ul className="program-grid">
                            {programs.map((program) => (
                                <li key={program.id} className="card program-card">
                                    {/* A link, not a div with onClick: the card must be
                                        reachable and activatable from the keyboard. */}
                                    <Link
                                        to={`/programs/${program.id}/edit`}
                                        className="program-card-link"
                                    >
                                        <span className="program-card-name">{program.name}</span>
                                        {program.description && (
                                            <span className="program-card-description">
                                                {program.description}
                                            </span>
                                        )}
                                    </Link>
                                    <div className="program-card-foot">
                                        <span className="meta-copy program-card-count">
                                            {program.sessionCount}{' '}
                                            {program.sessionCount === 1 ? 'session' : 'sessions'}
                                        </span>
                                        {/* Next cycle's block is this one with the
                                            loads moved. Retyping it is the slowest
                                            part of the job and where mistakes get
                                            made. */}
                                        <button
                                            type="button"
                                            className="icon-button"
                                            onClick={() => handleDuplicate(program)}
                                            disabled={duplicatingId === program.id}
                                            aria-label={`Duplicate ${program.name}`}
                                            title="Duplicate"
                                        >
                                            {duplicatingId === program.id ? '…' : '⧉'}
                                        </button>
                                        <button
                                            type="button"
                                            className="icon-button"
                                            onClick={() => handleDelete(program)}
                                            disabled={deletingId === program.id}
                                            aria-label={`Delete ${program.name}`}
                                        >
                                            {deletingId === program.id ? '…' : '✕'}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </BaseLayout>
    );
};

export default CreateProgram;
